import { concatLatestFrom } from '@ngrx/operators';
import { Action, createSelector, Store } from '@ngrx/store';
import { map, OperatorFunction } from 'rxjs';
import { Option } from '@sapphire/result';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  AdvancedFeasibilityFilters,
  Qualification,
  SurveyMarginFormState,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  CachedEntry,
  SubmittableForm,
  SurveyAudienceForm,
  SurveyLaunchSubmissionForm,
} from '../../../utils';
import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
  selectMarginForm,
  selectSuppliersMetaData,
  selectSurveyFormState,
} from '../../audience/survey-creation-audience.selectors';
import { selectSurveyId } from '../survey-creation-core.selectors';
import {
  selectFeasibilityCpi,
  selectPricing,
} from '../../insights/survey-creation-insights.selectors';
import { selectLaunchForm } from '../../launch/survey-creation-launch.selectors';
import { selectQualificationsRecord } from '../../reference/survey-creation-reference.selectors';
import { marginGateForm } from '../../audience/domain/margin-snapshot';

/**
 * Snapshot of the form + pricing state a create or a save gate starts from —
 * surveyId may be null. Shared by `SurveyAudienceCreateEffects` (audience,
 * create-only) and `SurveyCreationSaveEffects` (core, the multi-stage save
 * family): both stages read the exact same eight selectors before running
 * their own gate, so the read lives in one place instead of two.
 */
export interface SurveyFormContext {
  /** The save-* action that started this run; its props decide how it reports. */
  readonly trigger: Action;
  readonly surveyId: Nullable<number>;
  readonly audienceForm: SurveyAudienceForm;
  readonly launchForm: SurveyLaunchSubmissionForm;
  /** Only ever filled behind the `enableTargetMargin` service-operator gate. */
  readonly marginForm: SurveyMarginFormState;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
  /** More than one means a multi-locale pre-draft, never a standard survey. */
  readonly childrenCount: number;
  readonly qualificationsRecord: Record<string, CachedEntry<Qualification[]>>;
}

export function gatherSurveyFormContext(
  store: Store,
): OperatorFunction<Action, SurveyFormContext> {
  return (trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        store.select(selectSurveyId),
        store.select(selectSurveyFormState),
        store.select(selectLaunchForm),
        store.select(selectMarginForm),
        store.select(selectPricing),
        store.select(selectFeasibilityCpi),
        store.select(selectAdvancedFeasibilityFilters),
        store.select(selectAudienceChildren),
        store.select(selectQualificationsRecord),
        store.select(selectSuppliersMetaData),
      ]),
      map(
        ([
          trigger,
          surveyId,
          formState,
          launchFormState,
          marginState,
          pricingState,
          feasibilityCpi,
          advancedFeasibilityFilters,
          children,
          qualificationsRecord,
          suppliersMetaData,
        ]) => ({
          trigger,
          surveyId,
          audienceForm: SurveyAudienceForm.from(formState),
          launchForm: SurveyLaunchSubmissionForm.from(launchFormState),
          marginForm: marginState,
          pricing: {
            ...pricingState,
            feasibilityCpi,
            defaultPurePriceOverride:
              suppliersMetaData.defaultPurePriceOverride,
          } satisfies SurveyPricingSnapshot,
          advancedFeasibilityFilters,
          childrenCount: Object.keys(children).length,
          qualificationsRecord,
        }),
      ),
    );
}

// No child-count condition here, unlike the create gate: this runs only
// once `surveyId` is present, and a pre-draft locale has none. A saved MC
// family does have several children, and saving one of its locales is
// exactly what the buyer means.
export function isSaveable(
  audienceForm: SurveyAudienceForm,
  launchForm: SurveyLaunchSubmissionForm,
  marginForm: SurveyMarginFormState,
): boolean {
  return saveGateForms(audienceForm, launchForm, marginForm).every(
    (form) => form.valid,
  );
}

// A launch form the user never opened holds defaults, not a rejection, so it
// only gates the save — and only toasts — once it has actually been edited.
export function saveGateForms(
  audienceForm: SurveyAudienceForm,
  launchForm: SurveyLaunchSubmissionForm,
  marginForm: SurveyMarginFormState,
): SubmittableForm[] {
  return [
    ...(launchForm.isDirty ? [audienceForm, launchForm] : [audienceForm]),
    marginGateForm(marginForm),
  ];
}

export const selectIsSurveySaveable = createSelector(
  selectSurveyFormState,
  selectLaunchForm,
  selectMarginForm,
  (formState, launchFormState, marginForm) =>
    isSaveable(
      SurveyAudienceForm.from(formState),
      SurveyLaunchSubmissionForm.from(launchFormState),
      marginForm,
    ),
);

// Creating passes true to stay quiet until the required metadata is filled
// in; saving reports whatever is already invalid. Only the first blocking
// error is toasted, so the UX is one toast regardless of which form failed.
export function reportFormErrors(
  toastService: ToastService,
  forms: SubmittableForm[],
  suppressIncompleteErrors = false,
): void {
  Option.from(forms.find((form) => form.showErrors(suppressIncompleteErrors)))
    .andThen((form) => Option.from(form.error))
    .inspect((message) => toastService.error(message, { keepOpen: false }));
}

export function actingUser(authSelectors: AuthSelectorsFacade): {
  id: number;
  companyId: number;
} {
  const user = authSelectors.user().unwrap();
  return {
    id: user.id,
    companyId: authSelectors.activeCompanyId(),
  };
}
